import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import TablePagination from "../Utils/TablePagination";
import DriverAnalyticsTopSection from "./driverAnalytics/components/DriverAnalyticsTopSection";
import DriverPointsSection from "./driverAnalytics/components/DriverPointsSection";
import DriverReportsSection from "./driverAnalytics/components/DriverReportsSection";
import DriverAnalyticsSection from "./driverAnalytics/components/DriverAnalyticsSection";

import {
  CheckCircle,
  XCircle,
  ArrowDown,
  ArrowUp,
  CalendarDays,
  Sparkles,
  Clock,
  ThumbsUp,
  ThumbsDown,

  Activity,
  TrendingUp,
  TrendingDown,
  CalendarRange,
  Target,
  ShieldCheck,
  AlertTriangle,
  TimerReset,
  ListChecks,
  Shirt
} from "lucide-react";

import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";
import useReports from "../../Sevices/useReports";
import { Doughnut, Bar } from "react-chartjs-2";
import { useLocation } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import TaskDetailsModal from "../MainDashboard/MapDashBoard/TaskDetailsModal";
import useTasks from "../../Sevices/useTasks";
import { toast } from "sonner";

ChartJS.register(ArcElement, Tooltip, Legend);

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

// --------- FORMATTERS ---------
const fmt2 = (num) =>
  Number(num || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatMinutes = (minutes) => {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes === 0
    ? `${hours} h`
    : `${hours} h ${remainingMinutes} min`;
};

const formatDistance = (meters) => {
  if (!meters || isNaN(meters)) return "N/A";
  return meters < 1000
    ? `${Math.round(meters)} m`
    : `${(meters / 1000).toFixed(2)} km`;
};

const formatTime = (datetime) => {
  if (!datetime) return "N/A";
  return new Date(datetime).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};
const formatDateShort = (date) => {
  if (!date) return "N/A";

  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
};

const getPointTone = (value) => {
  const n = Number(value || 0);

  if (n > 0) {
    return {
      text: "text-emerald-700",
      bg: "bg-emerald-50",
      border: "border-emerald-200",
      icon: <TrendingUp size={16} />,
      prefix: "+",
    };
  }

  if (n < 0) {
    return {
      text: "text-red-700",
      bg: "bg-red-50",
      border: "border-red-200",
      icon: <TrendingDown size={16} />,
      prefix: "",
    };
  }

  return {
    text: "text-gray-600",
    bg: "bg-gray-50",
    border: "border-gray-200",
    icon: <Activity size={16} />,
    prefix: "",
  };
};
const getFactorMeta = (factorCode) => {
  const code = String(factorCode || "").toLowerCase();

  const map = {
    attendance: {
      label: "Attendance",
      desc: "Full day, half day and absent based points.",
      icon: <ShieldCheck size={18} />,
      cls: "bg-blue-50 text-blue-700 border-blue-200",
    },
    delivery: {
      label: "Delivery Time",
      desc: "Points based on order delay timing.",
      icon: <TimerReset size={18} />,
      cls: "bg-orange-50 text-orange-700 border-orange-200",
    },
    reliability: {
      label: "Reliability",
      desc: "Declined, missed and invalid flow deductions.",
      icon: <AlertTriangle size={18} />,
      cls: "bg-red-50 text-red-700 border-red-200",
    },
    ack: {
      label: "Acknowledgement",
      desc: "How fast the driver responds to an order.",
      icon: <Clock size={18} />,
      cls: "bg-violet-50 text-violet-700 border-violet-200",
    },
    duty: {
      label: "Duty Timing",
      desc: "Login timing and early off-duty behaviour.",
      icon: <CalendarRange size={18} />,
      cls: "bg-emerald-50 text-emerald-700 border-emerald-200",
    },
    success: {
      label: "Successful Orders",
      desc: "Points for successful order volume.",
      icon: <Target size={18} />,
      cls: "bg-cyan-50 text-cyan-700 border-cyan-200",
    },
  };

  return (
    map[code] || {
      label: factorCode || "Other",
      desc: "Point rule category.",
      icon: <ListChecks size={18} />,
      cls: "bg-gray-50 text-gray-700 border-gray-200",
    }
  );
};

const getDriverTier = (points) => {
  const p = Number(points || 0);

  if (p >= 900) {
    return {
      label: "Diamond",
      message: "Top performer",
      bar: "w-[90%]",
      cls: "bg-cyan-50 text-cyan-700 border-cyan-200",
    };
  }

  if (p >= 800) {
    return {
      label: "Gold",
      message: "Near best",
      bar: "w-[80%]",
      cls: "bg-yellow-50 text-yellow-700 border-yellow-200",
    };
  }

  if (p >= 700) {
    return {
      label: "Silver",
      message: "Improving well",
      bar: "w-[70%]",
      cls: "bg-slate-50 text-slate-700 border-slate-200",
    };
  }

  if (p >= 600) {
    return {
      label: "Bronze",
      message: "Stable performance",
      bar: "w-[60%]",
      cls: "bg-orange-50 text-orange-700 border-orange-200",
    };
  }

  return {
    label: "Iron",
    message: "Needs improvement",
    bar: "w-[56%]",
    cls: "bg-red-50 text-red-700 border-red-200",
  };
};

// --------- BADGE COMPONENT ---------
const Badge = ({ children, className = "", title }) => (
  <span
    title={title}
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${className}`}
  >
    {children}
  </span>
);

// --------- HELPERS (same as your sample UI) ---------
const parseEstimatedMinutes = (estimated) => {
  if (!estimated) return null;
  const m = String(estimated).toLowerCase().match(/(\d+)/);
  return m ? Number(m[1]) : null;
};

const normalizeTaskUpdate = (v) =>
  String(v || "")
    .trim()
    .toLowerCase();

const getWaitingMeta = (waiting) => {
  const w = String(waiting || "")
    .trim()
    .toLowerCase();
  if (!w) return { kind: "none", text: "No waiting record found" };

  if (w.startsWith("restaurant waited"))
    return { kind: "against_driver", text: waiting };
  if (w.startsWith("driver waited"))
    return { kind: "driver_favor", text: waiting };

  return { kind: "neutral", text: waiting };
};

// --------- CELL RENDERERS (UI copied from your design) ---------
const renderDeliveryTimeCell = (task) => {
  const deliveryMin = Number(task?.foodIsReadytoPickedup);
  const estMin = parseEstimatedMinutes(task?.foodIsReadytoPickedup);

  if (!Number.isFinite(deliveryMin)) return "N/A";

  const isLate = Number.isFinite(estMin) && deliveryMin > estMin;

  if (isLate) {
    return (
      <Badge
        title={`Late: ${deliveryMin} min vs est ${estMin} min`}
        className="bg-red-50 text-red-700 border border-red-200"
      >
        <ArrowDown size={14} />
        <span>{formatMinutes(deliveryMin)}</span>
      </Badge>
    );
  }

  return (
    <Badge
      title={
        Number.isFinite(estMin)
          ? `On time: ${deliveryMin} min vs est ${estMin} min`
          : `Delivery: ${deliveryMin} min`
      }
      className="bg-green-50 text-green-700 border border-green-200"
    >
      <ArrowUp size={14} />
      <span>{formatMinutes(deliveryMin)}</span>
    </Badge>
  );
};

function getViolationType(task) {
  const estimatedTimeMissing =
    task.estimated_time === null ||
    task.estimated_time === undefined ||
    task.estimated_time === "";

  const foodReadyToSuccess =
    task.foodIsReadytoSuccessful === null ||
      task.foodIsReadytoSuccessful === undefined ||
      task.foodIsReadytoSuccessful === ""
      ? null
      : Number(task.foodIsReadytoSuccessful);

  const pickupToSuccess =
    task.pickeduptoSuccessful === null ||
      task.pickeduptoSuccessful === undefined ||
      task.pickeduptoSuccessful === ""
      ? null
      : Number(task.pickeduptoSuccessful);

  //   Driver Location Off (Highest Priority)
  if (estimatedTimeMissing) {
    return "Driver Location Off";
  }

  //  Instant Delivery
  if (pickupToSuccess === 0) {
    return "Instant Delivery";
  }

  //  Pickup Missed
  if (foodReadyToSuccess !== null && pickupToSuccess === null) {
    return "Pickup Missed";
  }

  //  Restro Missed
  if (foodReadyToSuccess === null) {
    return "Restro Missed";
  }

  return "Normal";
}

const renderTaskUpdateCell = (taskUpdate) => {
  const t = normalizeTaskUpdate(taskUpdate);

  if (t === "daily") {
    return (
      <Badge className="bg-transparent text-blue-700 border border-blue-300">
        <CalendarDays size={14} />
        <span>Daily</span>
      </Badge>
    );
  }

  if (t === "extra") {
    return (
      <Badge className="bg-green-50 text-green-700 border border-green-200">
        <Sparkles size={14} />
        <span>Extra</span>
      </Badge>
    );
  }

  return (
    <Badge className=" text-gray-700 border border-gray-200">
      <Clock size={14} />
      <span>{taskUpdate ?? "N/A"}</span>
    </Badge>
  );
};

const renderWaitingTimeCell = (waiting_time) => {
  const meta = getWaitingMeta(waiting_time);

  if (meta.kind === "driver_favor") {
    return (
      <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
        <ThumbsUp size={14} />
        <span>{meta.text}</span>
      </Badge>
    );
  }

  if (meta.kind === "against_driver") {
    return (
      <Badge className="bg-amber-50 text-amber-800 border border-amber-200">
        <ThumbsDown size={14} />
        <span>{meta.text}</span>
      </Badge>
    );
  }

  if (meta.kind === "none")
    return <span className="text-gray-400">{meta.text}</span>;

  return (
    <Badge className=" text-gray-700 border border-gray-200">
      <Clock size={14} />
      <span>{meta.text}</span>
    </Badge>
  );
};
const pad2 = (num) => String(num).padStart(2, "0");

const toApiDate = (date) => {
  const d = new Date(date);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

const formatWeekLabelDate = (date) => {
  return new Date(date).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
};

const getMonday = (date) => {
  const d = new Date(date);
  const day = d.getDay(); // Sunday = 0, Monday = 1
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

const getWeekNumberFromMonday = (mondayDate) => {
  const yearStart = new Date(mondayDate.getFullYear(), 0, 1);
  const firstMonday = getMonday(yearStart);

  const diffMs = mondayDate.getTime() - firstMonday.getTime();
  return Math.floor(diffMs / (7 * 24 * 60 * 60 * 1000)) + 1;
};

const buildYearWeeks = (year) => {
  const weeks = [];

  let cursor = getMonday(new Date(year, 0, 1));

  // If first Monday belongs to previous year, still keep it because week can start in Dec and end in Jan.
  let index = 1;

  while (cursor.getFullYear() <= year || weeks.length < 53) {
    const start = new Date(cursor);
    const end = new Date(cursor);
    end.setDate(start.getDate() + 6);

    if (start.getFullYear() > year) break;

    weeks.push({
      id: `${toApiDate(start)}_${toApiDate(end)}`,
      weekNumber: index,
      fromDate: toApiDate(start),
      toDate: toApiDate(end),
      label: `Week ${index} • ${formatWeekLabelDate(start)} - ${formatWeekLabelDate(end)} ${end.getFullYear()}`,
      searchText: `week ${index} ${toApiDate(start)} ${toApiDate(end)} ${formatWeekLabelDate(start)} ${formatWeekLabelDate(end)}`.toLowerCase(),
    });

    cursor.setDate(cursor.getDate() + 7);
    index += 1;

    if (index > 54) break;
  }

  return weeks;
};

export default function DriverAnalytics() {
  const { driverId, username } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const today = new Date().toISOString().split("T")[0];
  const [fromDate, setFromDate] = useState(location.state?.fromDate || today);

  const [toDate, setToDate] = useState(location.state?.toDate || today);

  const {
    fetchDriverAnalytics,
    fetchDriverReports,
    fetchDriverPerformancePoints,
    fetchDriverDailyPerformancePoints,
    fetchDriverWeeklyPerformancePoints,
    fetchDriverMasterPointsData,
    updateDriverDressCodePoints,
    loading,
    taskLoading,
    pointsLoading,
    dailyPointsLoading,
    weeklyPointsLoading,
    masterPointsLoading,
    dressCodePointsLoading,
  } = useReports();

  const { fetchTaskLogs } = useTasks();

  const [analytics, setAnalytics] = useState(null);
  const [reports, setReports] = useState([]);

  const reportsPerPage = 15;
  const [currentPage, setCurrentPage] = useState(1);

  const totalCount = reports.length;
  const indexOfLast = currentPage * reportsPerPage;
  const indexOfFirst = indexOfLast - reportsPerPage;
  const currentReports = reports.slice(indexOfFirst, indexOfLast);

  const [activeTab, setActiveTab] = useState("analytics");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("ontime");
  const analyticsPerPage = 15;
  const [analyticsPage, setAnalyticsPage] = useState(1);

  const [expandedRows, setExpandedRows] = useState({});
  const [selectedTask, setSelectedTask] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showLogs, setShowLogs] = useState(false);
  const [assignmentLogs, setAssignmentLogs] = useState([]);
  const [statusChangeLogs, setStatusChangeLogs] = useState([]);

  const [driverPoints, setDriverPoints] = useState(null);
  const [dailyPoints, setDailyPoints] = useState([]);
  const [weeklyPoints, setWeeklyPoints] = useState([]);
  const [masterPointsData, setMasterPointsData] = useState([]);
  const [showPointsInfo, setShowPointsInfo] = useState(false);

  const currentYear = new Date().getFullYear();
  const [pointsYear, setPointsYear] = useState(currentYear);
  const [weekSearch, setWeekSearch] = useState("");
  const [showWeekDropdown, setShowWeekDropdown] = useState(false);

  const weekOptions = buildYearWeeks(pointsYear);

  const todayMonday = getMonday(new Date());
  const defaultWeekId = `${toApiDate(todayMonday)}_${toApiDate(
    new Date(todayMonday.getFullYear(), todayMonday.getMonth(), todayMonday.getDate() + 6),
  )}`;

  const [selectedWeekId, setSelectedWeekId] = useState(defaultWeekId);

  const selectedWeek =
    weekOptions.find((week) => week.id === selectedWeekId) || weekOptions[0];

  const filteredWeekOptions = weekOptions.filter((week) =>
    week.searchText.includes(weekSearch.toLowerCase()),
  );

  const toggleRow = (taskId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [taskId]: !prev[taskId],
    }));
  };

  const deliveryComparisonData = {
    labels: reports.map((task) => `Task ${task.pk_task_id}`),

    datasets: [
      {
        label: "Estimated Time (min)",
        data: reports.map((task) => Number(task.actual_estimated_time)),
        backgroundColor: "#3b82f6",
      },
      {
        label: "Actual Delivery Time (min)",
        data: reports.map((task) => Number(task.pickeduptoSuccessful)),
        backgroundColor: "#ef4444",
      },
    ],
  };

  const locationOffCount = reports.filter(
    (task) => getViolationType(task) === "Driver Location Off",
  ).length;

  const instantCount = reports.filter(
    (task) => getViolationType(task) === "Instant Delivery",
  ).length;

  const pickupMissedCount = reports.filter(
    (task) => getViolationType(task) === "Pickup Missed",
  ).length;

  const restroMissedCount = reports.filter(
    (task) => getViolationType(task) === "Restro Missed",
  ).length;

  const normalCount = reports.filter(
    (task) => getViolationType(task) === "Normal",
  ).length;
  const pointCategories = driverPoints
    ? [
      {
        label: "Attendance",
        key: "attendance_points",
        value: driverPoints.attendance_points,
        icon: <ShieldCheck size={18} />,
      },
      {
        label: "Delivery Time",
        key: "delivery_time_points",
        value: driverPoints.delivery_time_points,
        icon: <TimerReset size={18} />,
      },
      {
        label: "Declined / Missed",
        key: "declined_missed_points",
        value: driverPoints.declined_missed_points,
        icon: <AlertTriangle size={18} />,
      },
      {
        label: "Acknowledgement Delay",
        key: "acnowledgedment_delay_points",
        value: driverPoints.acnowledgedment_delay_points,
        icon: <Clock size={18} />,
      },
      {
        label: "Duty Timing",
        key: "duty_timing_points",
        value: driverPoints.duty_timing_points,
        icon: <CalendarRange size={18} />,
      },
      {
        label: "Successful Orders",
        key: "successful_orders_volume_points",
        value: driverPoints.successful_orders_volume_points,
        icon: <Target size={18} />,
      },
      {
        label: "Dress Code Points",
        key: "dress_code_points",
        value: driverPoints.dress_code_points,
        icon: <Shirt size={18} />,
      },
    ]
    : [];

  const totalPositivePoints = pointCategories
    .filter((item) => Number(item.value || 0) > 0)
    .reduce((sum, item) => sum + Number(item.value || 0), 0);

  const totalNegativePoints = pointCategories
    .filter((item) => Number(item.value || 0) < 0)
    .reduce((sum, item) => sum + Number(item.value || 0), 0);

  const bestDailyPoint =
    dailyPoints.length > 0
      ? Math.max(...dailyPoints.map((item) => Number(item.dailyPerformmancePoints || 0)))
      : 0;

  const worstDailyPoint =
    dailyPoints.length > 0
      ? Math.min(...dailyPoints.map((item) => Number(item.dailyPerformmancePoints || 0)))
      : 0;

  const pointsTier = getDriverTier(driverPoints?.points);

  const dailyPointsChartData = {
    labels: dailyPoints.map((item) => formatDateShort(item.created_on)),
    datasets: [
      {
        label: "Daily Performance Points",
        data: dailyPoints.map((item) =>
          Number(item.dailyPerformmancePoints || 0),
        ),
        backgroundColor: dailyPoints.map((item) =>
          Number(item.dailyPerformmancePoints || 0) >= 0 ? "#16a34a" : "#dc2626",
        ),
        borderRadius: 8,
      },
    ],
  };

  const handlePointsWeekSelect = (week) => {
    setSelectedWeekId(week.id);
    setWeekSearch("");
    setShowWeekDropdown(false);

    setDailyPoints([]);
    setWeeklyPoints([]);

    fetchDriverDailyPerformancePoints(
      driverId,
      week.fromDate,
      week.toDate,
      setDailyPoints,
    );

    fetchDriverWeeklyPerformancePoints(
      driverId,
      week.fromDate,
      week.toDate,
      setWeeklyPoints,
    );
  };

  const handleApply = () => {
    setCurrentPage(1);
    setAnalyticsPage(1);

    fetchDriverAnalytics(driverId, fromDate, toDate, setAnalytics);
    fetchDriverReports(driverId, fromDate, toDate, setReports);

    fetchDriverPerformancePoints(driverId, setDriverPoints);
    fetchDriverMasterPointsData(setMasterPointsData);

    if (selectedWeek?.fromDate && selectedWeek?.toDate) {
      setDailyPoints([]);
      setWeeklyPoints([]);

      fetchDriverDailyPerformancePoints(
        driverId,
        selectedWeek.fromDate,
        selectedWeek.toDate,
        setDailyPoints,
      );

      fetchDriverWeeklyPerformancePoints(
        driverId,
        selectedWeek.fromDate,
        selectedWeek.toDate,
        setWeeklyPoints,
      );
    }
  };

  const handleExport = () => {
    if (!reports.length) {
      toast.error("No reports available to export");
      return;
    }

    exportAnyJsonToExcel({
      data: reports,
      filePrefix: `Driver_Report_${driverId}`,
      sheetName: "Driver Reports",
    });
  };

  const showValue = (v, isAmount = false) => {
    if (v === null || v === undefined) return "N/A";
    return isAmount ? `₹ ${fmt2(v)}` : v;
  };

  const filteredReports = reports.filter((task) => {
    // Convert times properly
    const actual = Number(task.actual_estimated_time);
    const estimated = Number(task.estimated_time?.replace(/\D/g, ""));

    const isOnTime = actual <= estimated;
    const isLate = actual > estimated;

    if (statusFilter === "ontime" && !isOnTime) return false;
    if (statusFilter === "late" && !isLate) return false;

    if (selectedDate && task.delivery_date !== selectedDate) return false;

    const searchLower = searchTerm.toLowerCase();

    if (
      searchTerm &&
      !(
        task.pk_task_id?.toString().includes(searchLower) ||
        task.order_id?.toString().includes(searchLower) ||
        task.task_description?.toLowerCase().includes(searchLower)
      )
    )
      return false;

    return true;
  });

  const onTimeTasks = filteredReports.filter((task) => {
    if (!task.actual_estimated_time || !task.estimated_time) return false;

    const actual = Number(task.actual_estimated_time);
    const estimated = Number(task.estimated_time.replace(/\D/g, ""));

    return actual <= estimated;
  });

  const lateTasks = filteredReports.filter((task) => {
    if (!task.actual_estimated_time || !task.estimated_time) return false;

    const actual = Number(task.actual_estimated_time);
    const estimated = Number(task.estimated_time.replace(/\D/g, ""));

    return actual > estimated;
  });

  const notDeliveredTasks = filteredReports.filter(
    (task) => task.actual_estimated_time === null,
  );

  const statusBasedReports = filteredReports.filter((task) => {
    const actual = Number(task.actual_estimated_time);
    const estimated = Number(task.estimated_time?.replace(/\D/g, ""));

    switch (selectedStatus) {
      case "ontime":
        return (
          task.actual_estimated_time !== null &&
          task.actual_estimated_time !== undefined &&
          task.actual_estimated_time !== "" &&
          Number.isFinite(actual) &&
          Number.isFinite(estimated) &&
          actual <= estimated
        );

      case "late":
        return (
          task.actual_estimated_time !== null &&
          task.actual_estimated_time !== undefined &&
          task.actual_estimated_time !== "" &&
          Number.isFinite(actual) &&
          Number.isFinite(estimated) &&
          actual > estimated
        );
      case "notdelivered":
        return task.actual_estimated_time === null;

      case "locationoff":
        return getViolationType(task) === "Driver Location Off";

      case "instant":
        return getViolationType(task) === "Instant Delivery";

      case "pickupmissed":
        return getViolationType(task) === "Pickup Missed";

      case "restromissed":
        return getViolationType(task) === "Restro Missed";

      case "normal":
        return getViolationType(task) === "Normal";

      default:
        return true;
    }
  });

  const indexOfLastAnalytics = analyticsPage * analyticsPerPage;

  const indexOfFirstAnalytics = indexOfLastAnalytics - analyticsPerPage;

  const currentAnalyticsReports = statusBasedReports.slice(
    indexOfFirstAnalytics,
    indexOfLastAnalytics,
  );

  const StatusCard = ({ title, color, value, active, icon, onClick }) => {
    return (
      <div
        onClick={onClick}
        className={`
        cursor-pointer rounded-2xl p-5 transition-all duration-300
        border-l-4 ${color.border}
        ${color.bg}
        ${active ? `border-2 ${color.border} shadow-lg` : "border border-transparent shadow"}
        hover:shadow-md
      `}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${color.iconBg}`}>{icon}</div>

          <div>
            <p className="text-sm text-gray-600">{title}</p>
            <h3 className={`text-2xl font-bold ${color.text}`}>{value}</h3>
          </div>
        </div>
      </div>
    );
  };

  const onTimeCount = filteredReports.filter((task) => {
    if (!task.actual_estimated_time || !task.estimated_time) return false;

    const actualTime = Number(task.actual_estimated_time);
    const estimatedTime = Number(task.estimated_time.replace(/\D/g, ""));

    return actualTime <= estimatedTime;
  }).length;

  const lateCount = reports.filter((task) => {
    if (!task.actual_estimated_time || !task.estimated_time) return false;

    const actualTime = Number(task.actual_estimated_time);
    const estimatedTime = Number(task.estimated_time.replace(/\D/g, ""));

    return actualTime > estimatedTime;
  }).length;

  const notDeliveredCount = reports.filter(
    (task) => !task.actual_estimated_time,
  ).length;

  const deliveryPieData = {
    labels: [
      "On Time",
      "Late",
      "Driver Location Off",
      "Instant Delivery",
      "Pickup Missed",
      "Restro Missed",
      "Normal",
    ],
    datasets: [
      {
        data: [
          onTimeCount,
          lateCount,
          locationOffCount,
          instantCount,
          pickupMissedCount,
          restroMissedCount,
          normalCount,
        ],
        backgroundColor: [
          "#16a34a", // On Time - emerald
          "#dc2626", // Late - crimson
          "#d97706", // Driver Location Off - amber
          "#fb923c", // Instant - cyan
          "#7c3aed", // Pickup Missed - violet
          "#be123c", // Restro Missed - rose
          "#4f46e5", // Normal - indigo
        ],
        borderWidth: 1,
      },
    ],
  };

  useEffect(() => {
    setAnalyticsPage(1);
  }, [selectedStatus]);

  useEffect(() => {
    if (driverId && fromDate && toDate) {
      fetchDriverAnalytics(driverId, fromDate, toDate, setAnalytics);
      fetchDriverReports(driverId, fromDate, toDate, setReports);

      fetchDriverPerformancePoints(driverId, setDriverPoints);
      fetchDriverMasterPointsData(setMasterPointsData);

      if (selectedWeek?.fromDate && selectedWeek?.toDate) {
        setDailyPoints([]);
        setWeeklyPoints([]);

        fetchDriverDailyPerformancePoints(
          driverId,
          selectedWeek.fromDate,
          selectedWeek.toDate,
          setDailyPoints,
        );

        fetchDriverWeeklyPerformancePoints(
          driverId,
          selectedWeek.fromDate,
          selectedWeek.toDate,
          setWeeklyPoints,
        );
      }
    }
  }, [driverId]);

  const handleUpdateDressCodePoints = async (points) => {
    const performanceId = driverPoints?.pk_performmance_id;

    if (!performanceId) {
      toast.error("Performance ID not found");
      return false;
    }

    const success = await updateDriverDressCodePoints({
      id: performanceId,
      points,
    });

    if (success) {
      // refresh because backend may recalculate total points also
      await fetchDriverPerformancePoints(driverId, setDriverPoints);
    }

    return success;
  };

  return (
    <div className="p-6 min-h-screen space-y-5 ">

      <DriverAnalyticsTopSection
        username={username}
        driverId={driverId}
        fromDate={fromDate}
        toDate={toDate}
        setFromDate={setFromDate}
        setToDate={setToDate}
        handleApply={handleApply}
        handleExport={handleExport}
        navigate={navigate}
        loading={loading}
        analytics={analytics}
        showValue={showValue}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      {activeTab === "analytics" && (
        <DriverAnalyticsSection
          reports={reports}
          deliveryComparisonData={deliveryComparisonData}
          deliveryPieData={deliveryPieData}
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
          onTimeTasks={onTimeTasks}
          lateTasks={lateTasks}
          notDeliveredTasks={notDeliveredTasks}
          onTimeCount={onTimeCount}
          lateCount={lateCount}
          notDeliveredCount={notDeliveredCount}
          normalCount={normalCount}
          instantCount={instantCount}
          pickupMissedCount={pickupMissedCount}
          restroMissedCount={restroMissedCount}
          statusBasedReports={statusBasedReports}
          currentAnalyticsReports={currentAnalyticsReports}
          analyticsPage={analyticsPage}
          setAnalyticsPage={setAnalyticsPage}
          analyticsPerPage={analyticsPerPage}
          selectedTask={selectedTask}
          setSelectedTask={setSelectedTask}
          showModal={showModal}
          setShowModal={setShowModal}
          showLogs={showLogs}
          setShowLogs={setShowLogs}
          assignmentLogs={assignmentLogs}
          setAssignmentLogs={setAssignmentLogs}
          statusChangeLogs={statusChangeLogs}
          setStatusChangeLogs={setStatusChangeLogs}
          fetchTaskLogs={fetchTaskLogs}
          fromDate={fromDate}
          toDate={toDate}
          formatDistance={formatDistance}
          renderWaitingTimeCell={renderWaitingTimeCell}
          StatusCard={StatusCard}
          TablePagination={TablePagination}
          TaskDetailsModal={TaskDetailsModal}
        />
      )}
      {activeTab === "points" && (
        <DriverPointsSection
          pointsLoading={pointsLoading}
          driverPoints={driverPoints}
          pointsYear={pointsYear}
          setPointsYear={setPointsYear}
          currentYear={currentYear}
          selectedWeek={selectedWeek}
          selectedWeekId={selectedWeekId}
          setSelectedWeekId={setSelectedWeekId}
          weekSearch={weekSearch}
          setWeekSearch={setWeekSearch}
          showWeekDropdown={showWeekDropdown}
          setShowWeekDropdown={setShowWeekDropdown}
          filteredWeekOptions={filteredWeekOptions}
          handlePointsWeekSelect={handlePointsWeekSelect}
          buildYearWeeks={buildYearWeeks}
          setDailyPoints={setDailyPoints}
          fetchDriverDailyPerformancePoints={fetchDriverDailyPerformancePoints}
          driverId={driverId}
          totalPositivePoints={totalPositivePoints}
          totalNegativePoints={totalNegativePoints}
          pointsTier={pointsTier}
          pointCategories={pointCategories}
          dailyPoints={dailyPoints}
          weeklyPoints={weeklyPoints}
          weeklyPointsLoading={weeklyPointsLoading}
          dailyPointsLoading={dailyPointsLoading}
          dailyPointsChartData={dailyPointsChartData}
          bestDailyPoint={bestDailyPoint}
          worstDailyPoint={worstDailyPoint}
          getPointTone={getPointTone}
          getFactorMeta={getFactorMeta}
          masterPointsLoading={masterPointsLoading}
          masterPointsData={masterPointsData}
          showPointsInfo={showPointsInfo}
          setShowPointsInfo={setShowPointsInfo}
          Badge={Badge}
          formatMinutes={formatMinutes}
          handleUpdateDressCodePoints={handleUpdateDressCodePoints}
          dressCodePointsLoading={dressCodePointsLoading}
        />
      )}

      {activeTab === "reports" && (
        <DriverReportsSection
          taskLoading={taskLoading}
          reports={reports}
          filteredReports={filteredReports}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          selectedTask={selectedTask}
          setSelectedTask={setSelectedTask}
          showModal={showModal}
          setShowModal={setShowModal}
          showLogs={showLogs}
          setShowLogs={setShowLogs}
          assignmentLogs={assignmentLogs}
          setAssignmentLogs={setAssignmentLogs}
          statusChangeLogs={statusChangeLogs}
          setStatusChangeLogs={setStatusChangeLogs}
          fetchTaskLogs={fetchTaskLogs}
          fromDate={fromDate}
          toDate={toDate}
          expandedRows={expandedRows}
          toggleRow={toggleRow}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalCount={totalCount}
          reportsPerPage={reportsPerPage}
          formatDistance={formatDistance}
          formatMinutes={formatMinutes}
          formatTime={formatTime}
          fmt2={fmt2}
          renderWaitingTimeCell={renderWaitingTimeCell}
          renderTaskUpdateCell={renderTaskUpdateCell}
          renderDeliveryTimeCell={renderDeliveryTimeCell}
          TablePagination={TablePagination}
          TaskDetailsModal={TaskDetailsModal}
        />
      )}
    </div>
  );
}
